import React from 'react'
import Big from 'big.js'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GRAY_300, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { MUIButton } from 'components/common/MUIButton'
import { formatPrice } from 'utils/price/priceFormatter'

import { StruckPrice } from '../StruckPrice.component'
import type { ProductDrawerMode } from '../../store'
import { MUIDivider } from 'components/common/MUIDivider'
import { useBottomActionBarRef } from 'utils/hooks/bottomActionBarRegistry'

interface Props {
  /** Drawer mode — drives the primary button label ("Add" vs. "Save changes"). */
  mode: ProductDrawerMode
  /** Live line total (unitPrice × quantity) in Big form. */
  lineTotal: Big | null
  /** The same total without the saving, or null when the line carries none. */
  baseLineTotal?: Big | null
  /** Currency for the total. */
  currency: string | null
  /** True when the current selection is actually priceable. */
  isPriced: boolean
  /** Primary action — "Add to cart" or "Save changes". */
  onCommit: () => void
  /** Optional hard-disable of the commit button (e.g. missing required option). */
  isCommitDisabled?: boolean
  /**
   * Why the commit button is disabled, shown next to it. A dead button with no
   * reason reads as a broken drawer — the client cannot tell that the missing
   * step is theirs to complete.
   */
  commitHint?: string
  /** Names the cart packs that adding this one makes chargeable. */
  quotaNote?: string
  /**
   * When true, the primary CTA swaps to a "Select property first" button that
   * calls onSelectProperty instead of onCommit.  Used when no property is
   * selected in the store (catalogue-first flow, mode === 'new').
   */
  requiresPropertySelection?: boolean
  /** Called when the user clicks the "Select property first" gate button. */
  onSelectProperty?: () => void
  /** Set for a pack: the total sits left of the button under this label, as the PoC's bundle panel draws it. */
  subtotalLabel?: string
  /** Shown in place of the total while there is no price, when the drawer can say what is missing. */
  unpricedLabel?: string
}

export const DrawerFooter: React.FC<Props> = ({
  mode,
  lineTotal,
  baseLineTotal,
  currency,
  isPriced,
  onCommit,
  isCommitDisabled = false,
  commitHint,
  quotaNote,
  requiresPropertySelection = false,
  onSelectProperty,
  subtotalLabel,
  unpricedLabel,
}) => {
  const { t } = useTranslation(['purchase_flow_revamp'])
  const bottomActionBarRef = useBottomActionBarRef<HTMLDivElement>()

  const totalLabel = lineTotal && currency
    ? formatPrice(lineTotal, currency)
    : unpricedLabel ?? t('purchase_flow_revamp:drawer.not_available')

  const baseLabel = baseLineTotal && currency
    ? formatPrice(baseLineTotal, currency)
    : null

  return (
    <Stack
      ref={bottomActionBarRef}
      data-cy="pf-revamp-drawer-footer"
      sx={{
        borderTop: `1px solid ${GRAY_300}`,
      }}
    >

      {!!quotaNote &&
        <Typography data-cy="pf-revamp-drawer-quota-note" variant="text-xs" color={GRAY_700} sx={{ px: 2.4, pt: 1 }}>
          {quotaNote}
        </Typography>
      }

      {/* Button row — A#4: the panel's main (and only) footer action is adding
          the product. The former "Select an other property" affordance was
          removed per design (Baptiste); the drawer still closes via its header
          ✕ and backdrop click. */}
      <Stack direction="row" alignItems="center" justifyContent="flex-end" gap={1.6} sx={{ px: 2.4, py: 1.3 }}>

        {/* Total — live cost feedback before committing, left of the button. A pack shows its subtotal here instead. */}
        {!subtotalLabel &&
          <Stack gap={0.2} sx={{ mr: 'auto' }}>
            <Typography variant="text-xs" color={GRAY_700}>
              {t('purchase_flow_revamp:drawer.total_label')}
            </Typography>
            <Typography variant="text-md" fontWeight={550} color={GRAY_900}>
              <StruckPrice price={totalLabel} base={baseLabel} free={Boolean(lineTotal?.eq(0) && baseLineTotal)} />
            </Typography>
          </Stack>
        }

        {!!subtotalLabel &&
          <Stack data-cy="pf-revamp-drawer-bundle-subtotal" gap={0.2} sx={{ mr: 'auto' }}>
            <Typography variant="text-sm" color={GRAY_700}>{subtotalLabel}</Typography>
            <Typography variant="text-md" fontWeight={600} color={GRAY_900}>{lineTotal && currency ? formatPrice(lineTotal, currency) : null}</Typography>
          </Stack>
        }

        {!!commitHint && !requiresPropertySelection &&
          <Typography
            data-cy="pf-revamp-drawer-commit-hint"
            variant="text-sm"
            color={GRAY_700}
            sx={{ mr: subtotalLabel ? 'auto' : undefined }}
          >
            {commitHint}
          </Typography>
        }

        <Stack data-cy="pf-revamp-drawer-commit-wrap">

          {requiresPropertySelection
            ? (
              <MUIButton
                size="md"
                onClick={onSelectProperty}
                sx={{ whiteSpace: 'nowrap', fontWeight: 450, py: 0.8 }}
              >
                {t('purchase_flow_revamp:drawer.select_property_first_button')}
              </MUIButton>
            )
            : (
              <MUIButton
                size="md"
                disabled={!isPriced || isCommitDisabled}
                onClick={onCommit}
                sx={{ whiteSpace: 'nowrap', fontWeight: 450, py: 0.8 }}
              >
                {mode === 'new'
                  ? t('purchase_flow_revamp:drawer.add_button')
                  : t('purchase_flow_revamp:drawer.save_button')}
              </MUIButton>
            )
          }

        </Stack>

      </Stack>

    </Stack>
  )
}
